import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { integrationRegistry } from '@/integrations/registry';
import { apiService } from '@/services/api';
import { getOrCreateDefaultSocket } from '@/utils/socketUtils';
import { buildBindingPermissions } from '@/components/integrations/shared/bindingPayload';
import type { SpacePermissions } from '@/services/api';
import {
  ChromeWebStoreSetupPanelView,
  type ChromeWebStoreSetupPanelViewState,
} from './ChromeWebStoreSetupPanelView';

type PanelState = ChromeWebStoreSetupPanelViewState;

export interface ChromeWebStoreSetupPanelProps {
  spaceId: string;
  onDismiss: () => void;
  onBindingsChanged: () => void;
}

export function ChromeWebStoreSetupPanel({
  spaceId,
  onDismiss,
  onBindingsChanged,
}: ChromeWebStoreSetupPanelProps) {
  const { t } = useTranslation('integrations');
  const onBindingsChangedRef = useRef(onBindingsChanged);
  onBindingsChangedRef.current = onBindingsChanged;
  const [state, setState] = useState<PanelState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [connectionKey, setConnectionKey] = useState('');
  const [bindingId, setBindingId] = useState('');
  const [socketId, setSocketId] = useState('');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setState('loading');
      setErrorMessage(null);
      setConnectionKey('');
      setBindingId('');
      setSocketId('');

      try {
        const socket = await getOrCreateDefaultSocket(
          spaceId,
          integrationRegistry.chrome_extension
        );
        if (cancelled) return;

        const sid = socket.socket_id;
        setSocketId(sid);

        const existing = socket.integration_bindings?.find(
          (b) => b.integration_type === 'chrome_extension'
        );

        if (existing) {
          setBindingId(existing.binding_id);
          const key = existing.auth_config?.api_key;
          if (typeof key === 'string' && key.length > 0) {
            setConnectionKey(key);
            setState('existing');
          } else {
            setState('existing');
          }
          return;
        }

        const basePerms = buildBindingPermissions({
          flags: {
            can_read: true,
            can_write: true,
            can_search: true,
            can_delete: false,
          },
          categoriesComma: '',
          excludeTagsComma: '',
          minImportance: 1,
          requestsPerHour: 1000,
        });
        const permissions: SpacePermissions = {
          ...basePerms,
          rate_limits: {
            ...basePerms.rate_limits,
            requests_per_minute: 60,
          },
        };

        const created = await apiService.createIntegrationBinding(sid, {
          integration_name: t('chromeWebStore.bindingName'),
          integration_type: 'chrome_extension',
          auth_config: {
            description: t('chromeWebStore.authDescription'),
            response_format: 'json',
          },
          permissions,
        });

        if (cancelled) return;

        const key = created.auth_config?.api_key;
        if (typeof key !== 'string' || !key) {
          setErrorMessage(t('chromeWebStore.errors.noKeyInResponse'));
          setState('error');
          return;
        }

        setConnectionKey(key);
        setBindingId(created.binding_id);
        setState('ready');
        onBindingsChangedRef.current();
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setErrorMessage(msg);
        setState('error');
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [spaceId, t]);

  const handleRetry = () => {
    setState('loading');
    setErrorMessage(null);
    window.location.reload();
  };

  const handleRotate = async () => {
    if (!bindingId || !socketId) return;
    setState('loading');
    setErrorMessage(null);
    try {
      await apiService.deleteIntegrationBinding(socketId, bindingId);
      onBindingsChangedRef.current();
      window.location.reload();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErrorMessage(msg);
      setState('error');
    }
  };

  return (
    <ChromeWebStoreSetupPanelView
      state={state}
      errorMessage={errorMessage}
      connectionKey={connectionKey}
      bindingId={bindingId}
      socketId={socketId}
      onDismiss={onDismiss}
      onRetry={handleRetry}
      onRotate={() => void handleRotate()}
    />
  );
}
